import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Box,
  Badge,
  StatusIndicator,
  Button,
  ColumnLayout,
  Pagination,
  DateRangePicker,
  PropertyFilter,
} from '@cloudscape-design/components';
import { getAuditLog, getAuditCounts } from '../services/s3Service';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 25;

const OPERATION_COLORS = {
  INSERT: 'green',
  MODIFY: 'blue',
  LOGIN: 'grey',
  VISIT: 'grey',
  VIEW: 'blue',
  REVIEW_CALCULATOR: 'blue',
};

const getOperationLabel = (operation, tableName, newImage) => {
  if (operation === 'LOGIN') return 'Login';
  if (operation === 'VISIT') return 'Visit';
  if (operation === 'VIEW') return 'View';

  if (tableName === 'chronos_calculators') return 'Calculator Review';

  if (tableName === 'chronos_reviewer_feedback') {
    if (operation === 'INSERT') return 'Feedback';
    if (operation === 'MODIFY') return 'Reply';
  }

  if (tableName === 'chronos_review_state') {
    if (operation === 'INSERT') return 'Review';
    if (operation === 'MODIFY') {
      const newState = newImage?.review_state?.S;
      if (newState === '4-Failed') return 'Failed';
      return 'Revalidation';
    }
  }

  return operation;
};

const getOperationColor = (operation, tableName, newImage) => {
  const label = getOperationLabel(operation, tableName, newImage);
  if (label === 'Failed') return 'red';
  return OPERATION_COLORS[operation] || 'grey';
};

const extractProgram = (recordKey) => {
  if (!recordKey) return '—';
  const match = recordKey.match(/FR-([A-Z]+)-/);
  return match ? match[1] : '—';
};

const formatDate = (isoString) => {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const extractUser = (item) => {
  // Check top-level user field first (set by all new audit records)
  let raw = '';
  if (item.user) raw = item.user;
  else if (item.operation === 'LOGIN' && item.new_image) {
    const img = item.new_image;
    raw = img.email?.S || img.email || '';
  } else if (item.new_image) {
    const img = item.new_image;
    raw = img.triggered_by?.S || img.triggered_by
      || img.reviewer_alias?.S || img.reviewer_alias
      || img.reviewer_id?.S || img.reviewer_id || '';
  }
  if (!raw) return 'System';
  // Strip domain to get alias
  return raw.includes('@') ? raw.split('@')[0] : raw;
};

// Unmarshall DynamoDB typed attributes ({S: "val"}, {N: "1"}, {BOOL: true}) to plain values
const unmarshallValue = (v) => {
  if (v === null || v === undefined) return v;
  if (typeof v !== 'object') return v;
  if (v.S !== undefined) return v.S;
  if (v.N !== undefined) return Number(v.N);
  if (v.BOOL !== undefined) return v.BOOL;
  if (v.NULL) return null;
  if (v.L) return v.L.map(unmarshallValue);
  if (v.M) return unmarshallImage(v.M);
  // Already plain object or array
  if (Array.isArray(v)) return v.map(unmarshallValue);
  return unmarshallImage(v);
};

const unmarshallImage = (image) => {
  if (!image || typeof image !== 'object') return image;
  const result = {};
  for (const [key, val] of Object.entries(image)) {
    result[key] = unmarshallValue(val);
  }
  return result;
};

// Format record_key for display: "FR_id=FR-MAP-CASH-TVBM-xxx#reviewer_id=user" → linkable FR_id + readable parts
const formatRecordKey = (recordKey) => {
  if (!recordKey) return '—';
  const parts = recordKey.split('#');
  return parts.map((part, i) => {
    const eqIdx = part.indexOf('=');
    if (eqIdx === -1) return <span key={i}>{part}</span>;
    const value = part.substring(eqIdx + 1);
    // Make FR_id values clickable links
    if (part.startsWith('FR_id=') && value.startsWith('FR-')) {
      return (
        <span key={i}>
          {i > 0 && <br />}
          <Link to={`/fund-requests/${encodeURIComponent(value)}/details`}>{value}</Link>
        </span>
      );
    }
    return <span key={i}>{i > 0 && <br />}{value}</span>;
  });
};

const formatChangedFields = (item) => {
  const fields = item.changed_fields;
  if (!fields || fields.length === 0) return '—';
  if (item.operation !== 'MODIFY' || !item.old_image || !item.new_image) {
    return fields.join(', ');
  }
  return fields.map((field) => {
    const oldVal = item.old_image[field];
    const newVal = item.new_image[field];
    const fmt = (v) => {
      if (v === undefined || v === null) return '∅';
      if (typeof v === 'object' && (v.S || v.N || v.BOOL !== undefined)) {
        return v.S || v.N || String(v.BOOL);
      }
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      return s.length > 40 ? s.substring(0, 40) + '…' : s;
    };
    return `${field}: ${fmt(oldVal)} → ${fmt(newVal)}`;
  }).join('\n');
};

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    type: 'absolute',
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
};

const exportCsv = (items) => {
  const headers = ['Timestamp', 'Program', 'Operation', 'User', 'Record Key', 'Changed Fields'];
  const rows = items.map((item) => [
    item.timestamp || '',
    extractProgram(item.record_key),
    getOperationLabel(item.operation, item.table_name, item.new_image),
    extractUser(item),
    item.record_key || '',
    (item.changed_fields || []).join('; '),
  ]);
  const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const FILTERING_PROPERTIES = [
  { key: 'program', propertyLabel: 'Program', groupValuesLabel: 'Program values', operators: ['='] },
  { key: 'operation', propertyLabel: 'Operation', groupValuesLabel: 'Operation values', operators: ['='] },
  { key: 'user', propertyLabel: 'User', groupValuesLabel: 'User values', operators: ['=', ':'] },
  { key: 'record_key', propertyLabel: 'Record Key', groupValuesLabel: 'Record Key values', operators: ['=', ':'] },
];

// Map PropertyFilter operation display values back to DynamoDB values
const OPERATION_REVERSE = {
  'review': 'INSERT',
  'revalidation': 'MODIFY',
  'login': 'LOGIN',
  'visit': 'VISIT',
  'view': 'VIEW',
  'calculator review': 'REVIEW_CALCULATOR',
};

// Extract server-side filterable tokens — all filters go to the API
const extractServerFilters = (query) => {
  const filters = {};
  if (!query.tokens) return filters;
  for (const token of query.tokens) {
    if (token.propertyKey === 'program' && token.operator === '=') {
      filters.recordKey = `FR-${token.value.toUpperCase()}-`;
    } else if (token.propertyKey === 'operation' && token.operator === '=') {
      filters.operation = OPERATION_REVERSE[token.value.toLowerCase()] || token.value.toUpperCase();
    } else if (token.propertyKey === 'user') {
      filters.user = token.value;
    } else if (token.propertyKey === 'record_key') {
      filters.recordKey = token.value;
    }
  }
  return filters;
};

const ALL_TABLES = ['chronos_review_state', 'chronos_reviewer_feedback', 'cognito_auth', 'chronos_calculators'];

const AuditReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [currentPage, setCurrentPage] = useState(1);
  const [allItems, setAllItems] = useState([]);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [sortingColumn, setSortingColumn] = useState({ sortingField: 'timestamp' });
  const [sortingDescending, setSortingDescending] = useState(true);
  const [filterQuery, setFilterQuery] = useState({ tokens: [], operation: 'and' });
  const [counts, setCounts] = useState(null);

  const getDateFilters = useCallback(() => {
    const filters = {};
    if (dateRange && dateRange.type === 'absolute') {
      if (dateRange.startDate) filters.startDate = dateRange.startDate;
      if (dateRange.endDate) filters.endDate = dateRange.endDate;
    }
    return filters;
  }, [dateRange]);

  const serverFilters = useMemo(() => extractServerFilters(filterQuery), [filterQuery]);

  // Exhaustive per-partition fetch — same strategy as counts and CSV export
  const loadAllData = useCallback(async (srvFilters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const baseFilters = { ...getDateFilters(), ...srvFilters };

      // Fetch counts and records in parallel
      const countsPromise = getAuditCounts(baseFilters);

      let allRecords = [];
      for (const tableName of ALL_TABLES) {
        let token = null;
        do {
          const params = { ...baseFilters, tableName, pageSize: 100 };
          if (token) params.nextToken = token;
          const data = await getAuditLog(params);
          allRecords = [...allRecords, ...(data.auditRecords || [])];
          token = data.nextToken || null;
        } while (token);
      }

      allRecords.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
      setAllItems(allRecords);
      setCurrentPage(1);

      const countsData = await countsPromise;
      setCounts(countsData);
    } catch (err) {
      console.error('Failed to load audit data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getDateFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data when server-side filters or date range change
  useEffect(() => {
    loadAllData(serverFilters);
  }, [serverFilters, loadAllData]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredItems = allItems;

  const sortedItems = [...filteredItems].sort((a, b) => {
    const field = sortingColumn.sortingField;
    let aVal, bVal;
    if (field === 'user') {
      aVal = extractUser(a);
      bVal = extractUser(b);
    } else {
      aVal = a[field] || '';
      bVal = b[field] || '';
    }
    const cmp = String(aVal).localeCompare(String(bVal));
    return sortingDescending ? -cmp : cmp;
  });

  const totalPages = Math.ceil(sortedItems.length / PAGE_SIZE);
  const paginatedItems = sortedItems.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const c = counts || {};
  const byTblOp = c.byTableOperation || {};

  const toggleExpand = (auditId) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(auditId)) next.delete(auditId);
      else next.add(auditId);
      return next;
    });
  };

  const refreshAll = () => {
    loadAllData(serverFilters);
  };

  const handleExportAll = () => {
    exportCsv(sortedItems);
  };

  if (error) {
    return (
      <Container header={<Header variant="h1">Event History</Header>}>
        <Box textAlign="center" padding="xxl">
          <StatusIndicator type="error">{error}</StatusIndicator>
          <Box padding={{ top: 'm' }}>
            <Button onClick={refreshAll}>Retry</Button>
          </Box>
        </Box>
      </Container>
    );
  }

  const columnDefinitions = [
    { id: 'timestamp', header: 'Timestamp', cell: (item) => formatDate(item.timestamp), sortingField: 'timestamp', width: 200 },
    { id: 'program', header: 'Program', cell: (item) => { const p = extractProgram(item.record_key); return p === '—' ? '—' : <Badge color="blue">{p}</Badge>; }, sortingField: 'record_key' },
    { id: 'operation', header: 'Operation', cell: (item) => <Badge color={getOperationColor(item.operation, item.table_name, item.new_image)}>{getOperationLabel(item.operation, item.table_name, item.new_image)}</Badge>, sortingField: 'operation' },
    { id: 'user', header: 'User', cell: (item) => { const alias = extractUser(item); return alias === 'System' ? alias : <a href={`https://phonetool.amazon.com/users/${alias}`} target="_blank" rel="noopener noreferrer">{alias}</a>; }, sortingField: 'user' },
    { id: 'record_key', header: 'Record Key', cell: (item) => formatRecordKey(item.record_key), sortingField: 'record_key', width: 280 },
    {
      id: 'changed_fields', header: 'Changes',
      cell: (item) => {
        const text = formatChangedFields(item);
        if (text === '—') return '—';
        return <Box variant="code"><pre style={{ fontSize: '11px', margin: 0, whiteSpace: 'pre-wrap' }}>{text}</pre></Box>;
      },
    },
    {
      id: 'details', header: '', width: 120,
      cell: (item) => {
        const hasData = item.old_image || item.new_image;
        if (!hasData) return null;
        const isExpanded = expandedItems.has(item.audit_id);
        return <Button variant="inline-link" onClick={() => toggleExpand(item.audit_id)}>{isExpanded ? 'Hide' : 'View'} details</Button>;
      },
    },
  ];

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Event History</Header>

      <Container>
        <ColumnLayout columns={5} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Total Events</Box>
            <Box variant="awsui-value-large">{counts ? c.total : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Users</Box>
            <Box variant="awsui-value-large">{counts ? (c.unique_users || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Reviews</Box>
            <Box variant="awsui-value-large" color="text-status-success">{counts ? (byTblOp['chronos_review_state#INSERT'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Revalidations</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['chronos_review_state#MODIFY'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Failed</Box>
            <Box variant="awsui-value-large" color="text-status-error">{counts ? (byTblOp['chronos_review_state#FAILED'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Feedback</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['chronos_reviewer_feedback#INSERT'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Replies</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['chronos_reviewer_feedback#MODIFY'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Logins</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['cognito_auth#LOGIN'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Visits</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['cognito_auth#VISIT'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Views</Box>
            <Box variant="awsui-value-large" color="text-status-info">{counts ? (byTblOp['chronos_review_state#VIEW'] || 0) : '—'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Calculator Reviews</Box>
            <Box variant="awsui-value-large">{counts ? (byTblOp['chronos_calculators#REVIEW_CALCULATOR'] || 0) : '—'}</Box>
          </div>
        </ColumnLayout>
      </Container>

      <Table
        header={
          <Header variant="h2" counter={`(${counts ? c.total : filteredItems.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <DateRangePicker
                  value={dateRange}
                  onChange={({ detail }) => {
                    const v = detail.value;
                    if (v && v.type === 'absolute') {
                      setDateRange({
                        ...v,
                        startDate: v.startDate ? v.startDate.split('T')[0] : v.startDate,
                        endDate: v.endDate ? v.endDate.split('T')[0] : v.endDate,
                      });
                    } else {
                      setDateRange(v);
                    }
                  }}
                  relativeOptions={[]}
                  rangeSelectorMode="absolute-only"
                  dateOnly={true}
                  placeholder="Time range"
                  i18nStrings={{
                    absoluteModeTitle: 'Absolute',
                    cancelButtonLabel: 'Cancel', applyButtonLabel: 'Apply',
                    clearButtonLabel: 'Clear', startDateLabel: 'Start date',
                    endDateLabel: 'End date',
                  }}
                  isValidRange={(range) => {
                    if (range.type === 'absolute' && (!range.startDate || !range.endDate)) {
                      return { valid: false, errorMessage: 'Select start and end dates' };
                    }
                    return { valid: true };
                  }}
                />
                <Button iconName="refresh" onClick={refreshAll} loading={loading}>Refresh</Button>
                <Button iconName="download" onClick={handleExportAll}>Export CSV</Button>
              </SpaceBetween>
            }
          >
            Audit Events
          </Header>
        }
        loading={loading}
        loadingText="Loading audit log..."
        resizableColumns
        wrapLines
        items={paginatedItems}
        empty={
          <Box textAlign="center" padding="xxl">
            <Box variant="h3" color="text-status-inactive">No audit events found</Box>
            <Box variant="p" color="text-body-secondary" padding={{ top: 's' }}>
              Audit events are captured automatically when data changes occur.
            </Box>
          </Box>
        }
        columnDefinitions={columnDefinitions}
        sortingColumn={sortingColumn}
        sortingDescending={sortingDescending}
        onSortingChange={({ detail }) => { setSortingColumn(detail.sortingColumn); setSortingDescending(detail.isDescending); setCurrentPage(1); }}
        filter={
          <PropertyFilter
            query={filterQuery}
            onChange={({ detail }) => { setFilterQuery(detail); setCurrentPage(1); }}
            filteringProperties={FILTERING_PROPERTIES}
            filteringOptions={[
              { propertyKey: 'program', value: 'MAP' },
              { propertyKey: 'operation', value: 'Review' },
              { propertyKey: 'operation', value: 'Revalidation' },
              { propertyKey: 'operation', value: 'Login' },
              { propertyKey: 'operation', value: 'Visit' },
              { propertyKey: 'operation', value: 'View' },
              { propertyKey: 'operation', value: 'Calculator Review' },
            ]}
            filteringPlaceholder="Filter audit events by program, operation, user, or record key"
            i18nStrings={{
              filteringAriaLabel: 'Filter audit events', dismissAriaLabel: 'Dismiss',
              filteringPlaceholder: 'Filter audit events',
              groupValuesText: 'Values', groupPropertiesText: 'Properties',
              operatorsText: 'Operators', operationAndText: 'and', operationOrText: 'or',
              operatorLessText: 'Less than', operatorLessOrEqualText: 'Less than or equal',
              operatorGreaterText: 'Greater than', operatorGreaterOrEqualText: 'Greater than or equal',
              operatorContainsText: 'Contains', operatorDoesNotContainText: 'Does not contain',
              operatorEqualsText: 'Equals', operatorDoesNotEqualText: 'Does not equal',
              editTokenHeader: 'Edit filter', propertyText: 'Property',
              operatorText: 'Operator', valueText: 'Value',
              cancelActionText: 'Cancel', applyActionText: 'Apply',
              allPropertiesLabel: 'All properties',
              tokenLimitShowMore: 'Show more', tokenLimitShowFewer: 'Show fewer',
              clearFiltersText: 'Clear filters',
              removeTokenButtonAriaLabel: (token) => `Remove filter, ${token.propertyKey} ${token.operator} ${token.value}`,
              enteredTextLabel: (text) => `Use: "${text}"`,
            }}
          />
        }
        pagination={
          <Pagination
            currentPageIndex={currentPage}
            pagesCount={Math.max(totalPages, 1)}
            onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          />
        }
      />

      {paginatedItems.map((item) =>
        expandedItems.has(item.audit_id) ? (
          <Container key={item.audit_id} header={<Header variant="h3">Details: {item.record_key}</Header>}>
            <ColumnLayout columns={2}>
              {item.old_image && (
                <Box>
                  <Box variant="awsui-key-label">Old Image</Box>
                  <Box variant="code"><pre style={{ fontSize: '12px', maxHeight: '300px', overflow: 'auto' }}>{JSON.stringify(unmarshallImage(item.old_image), null, 2)}</pre></Box>
                </Box>
              )}
              {item.new_image && (
                <Box>
                  <Box variant="awsui-key-label">New Image</Box>
                  <Box variant="code"><pre style={{ fontSize: '12px', maxHeight: '300px', overflow: 'auto' }}>{JSON.stringify(unmarshallImage(item.new_image), null, 2)}</pre></Box>
                </Box>
              )}
            </ColumnLayout>
          </Container>
        ) : null
      )}
    </SpaceBetween>
  );
};

export default AuditReport;
